/**
 * Duplicate detection and next-number suggestions for invoice / offer numbers.
 *
 * Everybody writes their numbers differently ("RE-2026-05", "RE202605",
 * "2026/17", "INV 26-001") and the same user may run several schemes in
 * parallel. So instead of enforcing one format, every number is parsed into a
 * structure and only structures are compared. Two numbers are the same document
 * number when they describe the same series and the same counter value, no
 * matter how they were typed.
 *
 * A number is split into four parts:
 *   prefix  – leading letters ("RE", "INV")
 *   scope   – numeric parts in front of the counter (year, month, …)
 *   counter – the running number, i.e. the last numeric part
 *   suffix  – trailing letters ("a", "DE")
 *
 * prefix + scope + suffix form the series key: every series keeps its own
 * counter, which is why parallel schemes and yearly restarts work by
 * themselves. Separators, letter case and zero padding never enter the key.
 */

const YEAR_MIN = 1990;

const maxPlausibleYear = () => new Date().getFullYear() + 5;

const isYearDigits = (digits) =>
  digits.length === 4 &&
  Number(digits) >= YEAR_MIN &&
  Number(digits) <= maxPlausibleYear();

const tokenize = (raw) => {
  const tokens = [];
  const pattern = /([A-Za-z]+)|([0-9]+)|([^A-Za-z0-9]+)/g;
  let match;
  while ((match = pattern.exec(raw)) !== null) {
    if (match[1]) tokens.push({ kind: "alpha", value: match[1] });
    else if (match[2]) tokens.push({ kind: "num", value: match[2] });
    else tokens.push({ kind: "sep", value: match[3] });
  }
  return tokens;
};

// "RE202605" packs year and counter into one digit run. Split it so the counter
// can be recognised and incremented on its own.
const splitYearRuns = (tokens) => {
  const result = [];
  tokens.forEach((token) => {
    const head = token.kind === "num" ? token.value.slice(0, 4) : "";
    if (token.kind !== "num" || token.value.length < 5 || !isYearDigits(head)) {
      result.push(token);
      return;
    }
    result.push({ kind: "num", value: head });
    result.push({ kind: "num", value: token.value.slice(4) });
  });
  return result;
};

const padCounter = (value, width) => String(value).padStart(width || 1, "0");

/**
 * Renders a number template (as produced by parseDocumentNumber) with a given
 * counter, keeping the notation and zero padding of the original.
 */
export const renderNumber = (template, counter, width) =>
  (template || [])
    .map((token) =>
      token.kind === "counter" ? padCounter(counter, width) : token.value
    )
    .join("");

/** Human readable name of a series, e.g. "RE-2026-…". */
export const renderSeriesLabel = (template) =>
  (template || [])
    .map((token) => (token.kind === "counter" ? "…" : token.value))
    .join("");

// Plain counters ("1234") have no label worth showing.
const describeSeries = (template) => {
  const label = renderSeriesLabel(template);
  return label.replace(/…/g, "").trim() ? `series ${label}` : "this numbering";
};

export const parseDocumentNumber = (raw) => {
  const trimmed = String(raw || "").trim();
  const canonical = trimmed.toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!trimmed) {
    return { valid: false, raw: "", canonical: "" };
  }

  const tokens = splitYearRuns(tokenize(trimmed));
  const numIndexes = tokens.reduce(
    (acc, token, index) => (token.kind === "num" ? [...acc, index] : acc),
    []
  );
  if (!numIndexes.length) {
    return { valid: false, raw: trimmed, canonical };
  }

  let counterIndex = numIndexes[numIndexes.length - 1];
  // A trailing year belongs to the scope, not the counter ("RE-001-2026").
  if (isYearDigits(tokens[counterIndex].value) && numIndexes.length > 1) {
    const fallback = [...numIndexes]
      .reverse()
      .find((index) => !isYearDigits(tokens[index].value));
    if (fallback !== undefined) counterIndex = fallback;
  }

  const prefix = tokens
    .filter((token, index) => token.kind === "alpha" && index < counterIndex)
    .map((token) => token.value.toUpperCase())
    .join("");
  const suffix = tokens
    .filter((token, index) => token.kind === "alpha" && index > counterIndex)
    .map((token) => token.value.toUpperCase())
    .join("");
  // Numeric scope parts are compared by value so that "2026-5" and "2026-05"
  // land in the same series.
  const scope = tokens
    .filter((token, index) => token.kind === "num" && index !== counterIndex)
    .map((token) => String(Number(token.value)));

  const counterToken = tokens[counterIndex];
  const template = tokens.map((token, index) =>
    index === counterIndex ? { kind: "counter" } : token
  );

  return {
    valid: true,
    raw: trimmed,
    canonical,
    prefix,
    suffix,
    scope,
    counter: Number(counterToken.value),
    width: counterToken.value.length,
    template,
    seriesKey: `${prefix}|${scope.join(".")}|${suffix}`,
    // Same scheme across periods, e.g. RE-2025-… and RE-2026-….
    familyKey: `${prefix}|${suffix}`,
  };
};

const templateYear = (template) => {
  const yearToken = (template || []).find(
    (token) => token.kind === "num" && isYearDigits(token.value)
  );
  return yearToken ? Number(yearToken.value) : null;
};

const templateWithYear = (template, year) =>
  (template || []).map((token) =>
    token.kind === "num" && isYearDigits(token.value)
      ? { ...token, value: String(year) }
      : token
  );

/**
 * Turns the offers node of the database into the number entries of one kind.
 *
 * @param {object} offersById raw `/World/{userId}/offers` value
 * @param {"invoice"|"offer"} kind which number field to collect
 */
export const collectDocumentNumbers = (offersById, kind) =>
  Object.entries(offersById || {}).reduce((entries, [id, doc]) => {
    const isInvoice = doc?.documentType === "invoice";
    if (kind === "invoice" ? !isInvoice : isInvoice) return entries;

    const number = kind === "invoice" ? doc?.invoiceNumber : doc?.offerNumber;
    if (!String(number || "").trim()) return entries;

    const dated = doc?.invoiceDate ? new Date(doc.invoiceDate).getTime() : NaN;
    entries.push({
      id,
      number: String(number),
      label: doc?.title || doc?.customerName || "Untitled",
      customerName: doc?.customerName || "",
      date: dated || doc?.createdAt || 0,
    });
    return entries;
  }, []);

/**
 * Groups existing numbers into series so lookups during typing stay cheap.
 */
export const buildNumberIndex = (entries) => {
  const series = new Map();
  const byCanonical = new Map();

  (entries || []).forEach((entry) => {
    const parsed = parseDocumentNumber(entry.number);
    const record = { ...entry, parsed };

    if (parsed.canonical) {
      const sameText = byCanonical.get(parsed.canonical) || [];
      sameText.push(record);
      byCanonical.set(parsed.canonical, sameText);
    }
    if (!parsed.valid) return;

    let bucket = series.get(parsed.seriesKey);
    if (!bucket) {
      bucket = {
        key: parsed.seriesKey,
        familyKey: parsed.familyKey,
        template: parsed.template,
        width: parsed.width,
        maxCounter: -1,
        used: new Map(),
        entries: [],
        touchedAt: 0,
      };
      series.set(parsed.seriesKey, bucket);
    }

    bucket.entries.push(record);
    const sameCounter = bucket.used.get(parsed.counter) || [];
    sameCounter.push(record);
    bucket.used.set(parsed.counter, sameCounter);
    bucket.touchedAt = Math.max(bucket.touchedAt, entry.date || 0);
    if (parsed.counter > bucket.maxCounter) {
      // The highest number defines the notation new suggestions are built from.
      bucket.maxCounter = parsed.counter;
      bucket.template = parsed.template;
      bucket.width = parsed.width;
    }
  });

  return { series, byCanonical, total: (entries || []).length };
};

const rankedSeries = (index) =>
  [...index.series.values()].sort(
    (a, b) => b.touchedAt - a.touchedAt || b.entries.length - a.entries.length
  );

/**
 * Next number for an empty field: continues the series that was used last and
 * restarts at 1 when a year based series has rolled over into a new year.
 */
export const suggestNextNumber = (index) => {
  const buckets = rankedSeries(index);
  if (!buckets.length) return null;

  const bucket = buckets[0];
  const year = new Date().getFullYear();
  const bucketYear = templateYear(bucket.template);

  if (bucketYear && bucketYear < year) {
    const rolled = templateWithYear(bucket.template, year);
    const rolledFirst = renderNumber(rolled, 1, bucket.width);
    if (!index.series.has(parseDocumentNumber(rolledFirst).seriesKey)) {
      return rolledFirst;
    }
  }
  return renderNumber(bucket.template, bucket.maxCounter + 1, bucket.width);
};

const describeEntry = (entry) => {
  const parts = [entry.label, entry.customerName].filter(
    (part) => part && String(part).trim()
  );
  const unique = parts.filter((part, i) => parts.indexOf(part) === i);
  return unique.length ? unique.join(" · ") : "another document";
};

const dedupeById = (records) => {
  const seen = new Set();
  return records.filter((record) => {
    if (seen.has(record.id)) return false;
    seen.add(record.id);
    return true;
  });
};

/**
 * Checks a number while it is being typed.
 *
 * @returns {{status: string, severity: "error"|"warning"|"success"|"info"|"idle",
 *   message: string, suggestion: string|null, conflicts: Array,
 *   notation: {expected: string}|null}}
 */
export const analyzeDocumentNumber = (value, index, options = {}) => {
  const { currentId = null, noun = "number" } = options;
  const parsed = parseDocumentNumber(value);
  const isOther = (entry) => !currentId || entry.id !== currentId;

  const base = {
    status: "empty",
    severity: "idle",
    message: "",
    suggestion: null,
    conflicts: [],
    notation: null,
  };

  if (!parsed.raw) {
    return { ...base, suggestion: suggestNextNumber(index) };
  }

  const sameText = (index.byCanonical.get(parsed.canonical) || []).filter(isOther);

  if (!parsed.valid) {
    if (sameText.length) {
      return {
        ...base,
        status: "duplicate",
        severity: "error",
        message: `Already used by ${describeEntry(sameText[0])}.`,
        suggestion: suggestNextNumber(index),
        conflicts: sameText,
      };
    }
    return {
      ...base,
      status: "unparsable",
      severity: "info",
      message: `No running number found in this ${noun}, so duplicates can only be caught for identical spellings.`,
      suggestion: null,
    };
  }

  const bucket = index.series.get(parsed.seriesKey);
  const seriesName = describeSeries(bucket?.template || parsed.template);
  const nextFree = bucket ? bucket.maxCounter + 1 : null;
  const nextValue = bucket
    ? renderNumber(bucket.template, nextFree, bucket.width)
    : null;

  // Same spelling in a document whose number could not be parsed, plus every
  // document that means the same series and counter in any notation.
  const conflicts = dedupeById([
    ...(bucket?.used.get(parsed.counter) || []).filter(isOther),
    ...sameText.filter((entry) => !entry.parsed.valid),
  ]);

  if (conflicts.length) {
    return {
      ...base,
      status: "duplicate",
      severity: "error",
      message:
        `${conflicts[0].parsed.raw} already exists (${describeEntry(conflicts[0])}).` +
        (conflicts[0].parsed.raw !== parsed.raw
          ? " Same number, different spelling."
          : ""),
      suggestion: nextValue,
      conflicts,
    };
  }

  if (!bucket) {
    const sibling = rankedSeries(index).find(
      (candidate) => candidate.familyKey === parsed.familyKey
    );
    if (sibling) {
      return {
        ...base,
        status: "new-series",
        severity: "info",
        message: `Starts a new ${seriesName} — ${describeSeries(
          sibling.template
        )} ran up to ${renderNumber(
          sibling.template,
          sibling.maxCounter,
          sibling.width
        )}.`,
      };
    }
    return {
      ...base,
      status: "new-series",
      severity: "info",
      message: `First ${noun} in ${seriesName}.`,
    };
  }

  // Existing series use one spelling; point out deviations without blocking.
  const expected = renderNumber(bucket.template, parsed.counter, bucket.width);
  const notation = expected !== parsed.raw ? { expected } : null;

  if (parsed.counter < nextFree) {
    return {
      ...base,
      status: "behind",
      severity: "warning",
      message: `Still free, but ${seriesName} already runs up to ${renderNumber(
        bucket.template,
        bucket.maxCounter,
        bucket.width
      )}.`,
      suggestion: nextValue,
      notation,
    };
  }

  if (parsed.counter > nextFree) {
    const skipped = parsed.counter - nextFree;
    return {
      ...base,
      status: "gap",
      severity: "warning",
      message: `Leaves a gap of ${skipped} number${
        skipped === 1 ? "" : "s"
      } after ${renderNumber(bucket.template, bucket.maxCounter, bucket.width)}.`,
      suggestion: nextValue,
      notation,
    };
  }

  return {
    ...base,
    status: "next",
    severity: "success",
    message: `Next free number in ${seriesName}.`,
    notation,
  };
};
